import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useEffect, useContext, useMemo } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import {
  Tags,
  Plus,
  Save,
  Trash2,
  Pencil,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const GroupTypeMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [groupName, setGroupName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [groupList, setGroupList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(true);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const fetchGroupList = async (pg = 1, keyword = "") => {
    try {
      const res = await axios.get("http://nissi-india.com/api/grouptypeList", {
        params: {
          page: pg,
          pageSize: limit,
          search: keyword,
        },
        headers: commonHeaders,
      });

      setGroupList(res.data?.records || []);
      setTotalPages(res.data?.totalPages || 1);
      setTotalRecords(res.data?.totalRecords || 0);
    } catch (err) {
      console.error("Failed to fetch group type list:", err);
      toast.error("Failed to fetch group type list");
    }
  };

  useEffect(() => {
    fetchGroupList(page, search);
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    fetchGroupList(1, search);
  };

  const handleAddNew = () => {
    setEditId(null);
    setGroupName("");
    setRemarks("");
    setSelectedRows([]);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!groupName.trim()) {
      toast.warning("Group Type Name is required");
      return;
    }

    if (!userId) {
      toast.warning("User information missing");
      return;
    }

    const payload = {
      grouptypename: groupName,
      remarks,
      userid: userId,
    };

    try {
      if (editId) {
        await axios.put(
          `http://nissi-india.com/api/updateGroupType/${editId}`,
          payload,
          { headers: commonHeaders }
        );
        toast.success("Group Type updated successfully");
      } else {
        await axios.post("http://nissi-india.com/api/addGroupType", payload, {
          headers: commonHeaders,
        });
        toast.success("Group Type added successfully");
      }

      handleAddNew();
      fetchGroupList(page, search);
    } catch (err) {
      console.error("Failed to save group type:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to save group type"
      );
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await axios.get(
        `http://nissi-india.com/api/groupType_fetchEdit/${id}`,
        { headers: commonHeaders }
      );

      const data = res.data?.groupType || res.data;

      if (data) {
        setEditId(data.fld_grouptypeid);
        setGroupName(data.fld_grouptypename || "");
        setRemarks(data.fld_remarks || "");
        setShowForm(true);
      } else {
        toast.error("Group Type data not found");
      }
    } catch (err) {
      console.error("Edit fetch error:", err);
      toast.error("Failed to fetch group type data");
    }
  };

  const handleSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      toast.warning("No group types selected for deletion");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedRows.length} group type(s)?`
    );
    if (!confirmDelete) return;

    try {
      await axios.delete("http://nissi-india.com/api/deleteGroupTypes", {
        data: { ids: selectedRows },
        headers: commonHeaders,
      });

      toast.success("Selected group types deleted");
      setSelectedRows([]);
      fetchGroupList(page, search);
    } catch (err) {
      console.error("Delete group type error:", err);
      toast.error("Failed to delete group types");
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Account Group Type Master</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* Form Header */}
          <div
            className={`px-6 py-4 border-b flex items-center justify-between ${
              isDark ? "border-slate-700/50 bg-slate-900/40" : "border-slate-200/50 bg-white/60"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <Tags className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {editId ? "Edit Group Type" : "Add New Group Type"}
                </h3>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  {editId ? "Update group type details" : "Create a new account group type"}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowForm(!showForm)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              {showForm ? "Hide Form" : "Show Form"}
            </button>
          </div>

          {showForm && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>
                    Group Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Enter group type name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Remarks</label>
                  <textarea
                    className={`${inputClass} resize-none`}
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional remarks..."
                  />
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={handleAddNew}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Plus size={16} />
                  Add New
                </button>

                <button
                  onClick={handleSave}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
                >
                  <Save size={16} />
                  {editId ? "Update Group Type" : "Save Group Type"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Group Type List</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {totalRecords} records
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search group type..."
                    className={`pl-9 pr-3 py-2 rounded-lg border text-sm w-56 ${
                      isDark
                        ? "bg-slate-800/60 border-slate-600 text-white"
                        : "bg-white border-slate-300"
                    }`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </div>

                <button
                  onClick={handleSearch}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
                >
                  <Search size={16} />
                  Search
                </button>

                <button
                  onClick={handleDeleteSelected}
                  disabled={selectedRows.length === 0}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition ${
                    selectedRows.length > 0
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  <Trash2 size={16} />
                  Delete ({selectedRows.length})
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Group Type</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Select</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Edit</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {groupList.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Tags size={40} className="text-gray-400" />
                        <span>No group types found</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  groupList.map((item, idx) => {
                    const isSelected = selectedRows.includes(item.grouptypeid);

                    return (
                      <tr
                        key={item.grouptypeid}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        } ${isSelected ? `bg-${baseColor}-50 dark:bg-${baseColor}-900/20` : ""}`}
                      >
                        <td className="px-4 py-3 font-medium">
                          {(page - 1) * limit + idx + 1}
                        </td>
                        <td className="px-4 py-3 font-medium">{item.grouptype}</td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelect(item.grouptypeid)}
                            className="w-4 h-4 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleEdit(item.grouptypeid)}
                            className="p-1.5 rounded-full bg-yellow-100 text-yellow-600 hover:bg-yellow-200 transition"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Showing {totalRecords === 0 ? 0 : (page - 1) * limit + 1} to{" "}
                {Math.min(page * limit, totalRecords)} of {totalRecords} results
              </div>

              <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  <ChevronLeft size={16} />
                  Prev
                </button>

                {[...Array(Math.min(totalPages, 5))].map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setPage(idx + 1)}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      page === idx + 1
                        ? `bg-${baseColor}-${baseShade} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page === totalPages}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default GroupTypeMaster;